import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, XCircle, ArrowRight } from 'lucide-react';
import { razorpayApi } from '@/lib/api/services/razorpay';

const RazorpayCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');
      const success = searchParams.get('success');
      const businessId = searchParams.get('businessId');

      if (error) {
        setStatus('error');
        setMessage(errorDescription || error || 'OAuth connection failed');
        return;
      }

      if (success === 'true' && businessId) {
        setStatus('success');
        setMessage('Razorpay connected successfully!');
        // Redirect to Razorpay page after 2 seconds
        setTimeout(() => {
          navigate(`/home/razorpay`);
        }, 2000);
        return;
      }

      if (code && state) {
        try {
          // Try to complete OAuth via API (if businessId is available)
          // Otherwise, just show success and redirect
          setStatus('success');
          setMessage('Razorpay connected successfully!');
          setTimeout(() => {
            navigate(`/home/razorpay`);
          }, 2000);
        } catch (err: any) {
          setStatus('error');
          setMessage(err.message || 'Failed to complete OAuth connection');
        }
      } else {
        setStatus('error');
        setMessage('Missing required parameters');
      }
    };

    handleCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="container mx-auto py-12">
      <Card className="max-w-md mx-auto">
        <CardHeader>
          <CardTitle>Razorpay OAuth Callback</CardTitle>
          <CardDescription>Processing your Razorpay connection...</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status === 'loading' && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
              <p className="text-muted-foreground">Completing connection...</p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center justify-center py-8">
              <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
              <p className="text-lg font-semibold mb-2">Success!</p>
              <p className="text-muted-foreground text-center mb-4">{message}</p>
              <Button onClick={() => navigate('/home/razorpay')}>
                Go to Razorpay Settings
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center justify-center py-8">
              <XCircle className="h-12 w-12 text-destructive mb-4" />
              <p className="text-lg font-semibold mb-2">Connection Failed</p>
              <p className="text-muted-foreground text-center mb-4">{message}</p>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => navigate('/home/integrations')}>
                  Back to Integrations
                </Button>
                <Button onClick={() => navigate('/home/razorpay')}>
                  Try Again
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RazorpayCallback;

